import { FormValidationErrors } from '../../@types/form-validation-errors';
import { useState } from 'react';

/** Converts a string or exception to a FormValidationErrors object. */
function exceptionAsValidationErrors(
  exception: Error | string | FormValidationErrors
): FormValidationErrors {
  if (typeof exception === 'string') {
    return { custom: [exception] };
  } else if (exception instanceof Error) {
    return { custom: [exception.message] };
  }

  return exception;
}

const NO_VALIDATION_ERRORS: FormValidationErrors = {};

/**
 * Provides a hook to catch and track form validation. It returns an object containing
 * the validationErrors and an async function to track validation by catching exceptions.
 * Validation errors can be the form of `{ field: ['error'] }` or can be an exception.
 *
 *     const [validationErrors, withFormValidation] = useFormValidation();
 *
 * Note: this hook does NOT run validation on the form, rather it catches
 * exceptions and uses them to render validation messages. If you want to validate the form,
 * see `src/validate.ts` which will throw validation errors as exceptions.
 */
export default function useFormValidation(): [
  FormValidationErrors,
  (callback: () => Promise<void>) => Promise<void>
] {
  const [validationErrors, setValidationErrors] = useState(NO_VALIDATION_ERRORS);

  /**
   * Wraps a callback to catch any errors and set them as validation errors on the form.
   */
  async function withFormValidation(callback: () => Promise<void>) {
    try {
      await callback();
      setValidationErrors(NO_VALIDATION_ERRORS);
    } catch (exception) {
      setValidationErrors(exceptionAsValidationErrors(exception));
    }
  }

  return [validationErrors, withFormValidation];
}
